/**
 * Space cipher orchestration (client-side).
 *
 * Thin, framework-agnostic layer over `@adieuu/crypto`'s per-Space cipher
 * verification primitives. Its two jobs:
 *
 * 1. **Per-Space key cache** — deriving a per-Space key runs a memory-hard
 *    Argon2id (see `deriveSpaceCipherKey`). That cost must be paid only once per
 *    (Cipher, Space); this module caches the derived key in memory so per-message
 *    crypto reuses the fast AEAD key.
 * 2. **Local `spaceId -> cipherId` link** — an in-memory map of which Cipher a
 *    Space is bound to, so we never iterate every Cipher for every message.
 *    Durable persistence lives on `StoredCipher.spaceIds` (bookmarks) in the
 *    cipher store; call {@link registerSpaceCipherLink} after persisting, and
 *    hydrate this map from the store on load.
 *
 * All state here is in-memory only and holds derived key material, so it MUST be
 * cleared on logout / identity switch / local wipe via {@link clearSpaceCipherState}.
 *
 * @module services/spaceCipherService
 */

import {
  createCipherCheck,
  verifyCipherCheck,
  decryptCipherCheckKnownValue,
  deriveSpaceCipherKey,
  randomBytes,
  toHex,
  type CommunityCipher,
  type SpaceCipherCheck,
} from '@adieuu/crypto';

/** Cache of per-Space AEAD keys, keyed by `${spaceId}:${cipherId}`. */
const spaceKeyCache = new Map<string, Uint8Array>();

/** Local link of `spaceId -> local cipher id` (the cipher store's `StoredCipher.id`). */
const spaceCipherLinks = new Map<string, string>();

/** Local link of `channelId -> local cipher id` for per-channel Cipher overrides. */
const channelCipherLinks = new Map<string, string>();

/**
 * Epoch bumped whenever space/channel/category cipher links change.
 * React consumers subscribe via {@link subscribeCipherLinks} +
 * {@link getCipherLinkEpoch} (`useSyncExternalStore`) so UI re-resolves
 * after hydrate, bookmark, or detect without a local version counter.
 */
let cipherLinkEpoch = 0;
const cipherLinkListeners = new Set<() => void>();

let spaceKeyEpoch = 0;
const spaceKeyListeners = new Set<() => void>();

function notifyCipherLinkListeners(): void {
  cipherLinkEpoch += 1;
  for (const listener of cipherLinkListeners) {
    listener();
  }
}

function notifySpaceKeyListeners(): void {
  spaceKeyEpoch += 1;
  for (const listener of spaceKeyListeners) {
    listener();
  }
}

/** Subscribe to cipher-link map changes (for `useSyncExternalStore`). */
export function subscribeCipherLinks(onStoreChange: () => void): () => void {
  cipherLinkListeners.add(onStoreChange);
  return () => {
    cipherLinkListeners.delete(onStoreChange);
  };
}

/** Current cipher-link epoch (for `useSyncExternalStore` getSnapshot). */
export function getCipherLinkEpoch(): number {
  return cipherLinkEpoch;
}

/** Manually advance the link epoch (e.g. after an external recover path). */
export function bumpCipherLinkEpoch(): void {
  notifyCipherLinkListeners();
}

/** Subscribe to per-Space key-cache changes (for `useSyncExternalStore`). */
export function subscribeSpaceKeys(onStoreChange: () => void): () => void {
  spaceKeyListeners.add(onStoreChange);
  return () => {
    spaceKeyListeners.delete(onStoreChange);
  };
}

/** Current space-key cache epoch (for `useSyncExternalStore` getSnapshot). */
export function getSpaceKeyEpoch(): number {
  return spaceKeyEpoch;
}

function keyCacheKey(spaceId: string, cipherId: string): string {
  return `${spaceId}:${cipherId}`;
}

/**
 * Generates a client-side Space id (24-hex, ObjectId-compatible).
 *
 * A Space's cipher challenge is bound to its `_id`, so an E2EE create must know
 * the id before the atomic server create. The server accepts this id verbatim
 * (validated as an ObjectId) so the challenge computed here stays valid.
 */
export function generateSpaceId(): string {
  return toHex(randomBytes(12));
}

/**
 * Returns the per-Space key for a Cipher, deriving (and caching) it on first use.
 *
 * The first call for a given (Cipher, Space) runs Argon2id; subsequent calls are
 * a Map lookup.
 */
export async function getSpaceKey(
  cipher: CommunityCipher,
  spaceId: string,
): Promise<Uint8Array> {
  const cacheKey = keyCacheKey(spaceId, cipher.cipherId);
  const cached = spaceKeyCache.get(cacheKey);
  if (cached) return cached;

  const derived = await deriveSpaceCipherKey(cipher, spaceId);
  spaceKeyCache.set(cacheKey, derived);
  notifySpaceKeyListeners();
  return derived;
}

/**
 * Returns the cached per-Space key without deriving. Null when Argon2 has not
 * completed for this (cipher, space) pair yet.
 */
export function peekSpaceKey(cipher: CommunityCipher, spaceId: string): Uint8Array | null {
  return spaceKeyCache.get(keyCacheKey(spaceId, cipher.cipherId)) ?? null;
}

/**
 * Creates a blind-relay challenge for a Space, reusing the cached per-Space key.
 * Upload the result to the server when creating the Space (or channel).
 */
export async function createSpaceCipherCheck(
  cipher: CommunityCipher,
  spaceId: string,
  options?: { knownValue?: string },
): Promise<SpaceCipherCheck> {
  const spaceKey = await getSpaceKey(cipher, spaceId);
  return createCipherCheck(cipher, spaceId, { ...options, spaceKey });
}

/**
 * Verifies a Cipher against a Space's challenge, reusing the cached per-Space key.
 */
export async function verifySpaceCipherCheck(
  cipher: CommunityCipher,
  spaceId: string,
  check: SpaceCipherCheck,
): Promise<boolean> {
  const spaceKey = await getSpaceKey(cipher, spaceId);
  return verifyCipherCheck(cipher, spaceId, check, { spaceKey });
}

/**
 * Decrypts the public challenge to `knownValue` for a `cipherRequired` join
 * proof. Never sends Cipher entropy — only the solved challenge plaintext.
 */
export async function solveSpaceCipherCheck(
  cipher: CommunityCipher,
  spaceId: string,
  check: Pick<SpaceCipherCheck, 'encryptedKnownValue' | 'nonce'>,
): Promise<string | null> {
  const spaceKey = await getSpaceKey(cipher, spaceId);
  return decryptCipherCheckKnownValue(cipher, spaceId, check, { spaceKey });
}

/**
 * Join-time detection: returns the first candidate Cipher that decrypts the
 * Space's challenge, or null when none match. Matching (Cipher, Space) keys are
 * cached as a side effect; failed candidates are evicted after each check.
 */
export async function detectSpaceCipher(
  ciphers: readonly CommunityCipher[],
  spaceId: string,
  check: SpaceCipherCheck,
): Promise<CommunityCipher | null> {
  for (const cipher of ciphers) {
    if (await verifySpaceCipherCheck(cipher, spaceId, check)) {
      return cipher;
    }
    evictSpaceKey(spaceId, cipher.cipherId);
  }
  return null;
}

/** Records the local `spaceId -> local cipher id` link (persist via the cipher store separately). */
export function registerSpaceCipherLink(spaceId: string, cipherLocalId: string): void {
  const prev = spaceCipherLinks.get(spaceId);
  spaceCipherLinks.set(spaceId, cipherLocalId);
  if (prev !== cipherLocalId) notifyCipherLinkListeners();
}

/** Returns the local cipher id bound to a Space, or null if unknown. */
export function getSpaceCipherLink(spaceId: string): string | null {
  return spaceCipherLinks.get(spaceId) ?? null;
}

/** Removes the local link for a Space (e.g. on leave). */
export function removeSpaceCipherLink(spaceId: string): void {
  if (!spaceCipherLinks.delete(spaceId)) return;
  notifyCipherLinkListeners();
}

/** Drops remembered channel links. Space bookmarks and derived keys stay. */
export function clearChannelCipherLinks(): void {
  if (channelCipherLinks.size === 0) return;
  channelCipherLinks.clear();
  notifyCipherLinkListeners();
}
export function registerChannelCipherLink(channelId: string, cipherLocalId: string): void {
  const prev = channelCipherLinks.get(channelId);
  channelCipherLinks.set(channelId, cipherLocalId);
  if (prev !== cipherLocalId) notifyCipherLinkListeners();
}

/** Returns the local cipher id bound to a channel, or null if unknown. */
export function getChannelCipherLink(channelId: string): string | null {
  return channelCipherLinks.get(channelId) ?? null;
}

/** Removes the local link for a channel. */
export function removeChannelCipherLink(channelId: string): void {
  if (!channelCipherLinks.delete(channelId)) return;
  notifyCipherLinkListeners();
}

const categoryCipherLinks = new Map<string, string>();

/** Records a per-category default Cipher link (in-memory). */
export function registerCategoryCipherLink(categoryId: string, cipherLocalId: string): void {
  const prev = categoryCipherLinks.get(categoryId);
  categoryCipherLinks.set(categoryId, cipherLocalId);
  if (prev !== cipherLocalId) notifyCipherLinkListeners();
}

/** Returns the local cipher id bound to a category, or null if unknown. */
export function getCategoryCipherLink(categoryId: string): string | null {
  return categoryCipherLinks.get(categoryId) ?? null;
}

/** Removes the local link for a category. */
export function removeCategoryCipherLink(categoryId: string): void {
  if (!categoryCipherLinks.delete(categoryId)) return;
  notifyCipherLinkListeners();
}

/** Evicts a single cached per-Space key. */
export function evictSpaceKey(spaceId: string, cipherId: string): void {
  if (!spaceKeyCache.delete(keyCacheKey(spaceId, cipherId))) return;
  notifySpaceKeyListeners();
}

/** Evicts all cached per-Space keys for a Space. */
export function clearSpaceKeyCacheForSpace(spaceId: string): void {
  const prefix = `${spaceId}:`;
  let removed = false;
  for (const key of spaceKeyCache.keys()) {
    if (key.startsWith(prefix)) {
      spaceKeyCache.delete(key);
      removed = true;
    }
  }
  if (removed) notifySpaceKeyListeners();
}

/**
 * Clears all in-memory Space cipher state (derived keys + links).
 * MUST be called on logout, identity switch, and local wipe.
 */
export function clearSpaceCipherState(): void {
  const hadLinks =
    spaceCipherLinks.size > 0 ||
    channelCipherLinks.size > 0 ||
    categoryCipherLinks.size > 0;
  const hadKeys = spaceKeyCache.size > 0;
  spaceKeyCache.clear();
  spaceCipherLinks.clear();
  channelCipherLinks.clear();
  categoryCipherLinks.clear();
  if (hadLinks) notifyCipherLinkListeners();
  if (hadKeys) notifySpaceKeyListeners();
}
